(function () {
  'use strict';

  const Engine = window.GreenfieldAnchorMatchEngine;
  if (!Engine || customElements.get('greenfield-anchor-match')) return;

  const SESSION_KEY = 'greenfield-anchor-match:v2';
  const QUESTIONS = [
    {
      key: 'boat_type',
      title: 'What kind of boat do you have?',
      hint: 'Pick the closest match.',
      options: [
        ['pwc_paddle', 'PWC or paddle craft', 'Jet ski, kayak, canoe'],
        ['fishing_skiff', 'Fishing boat or skiff', 'Bass boat, jon boat, center console'],
        ['runabout', 'Runabout or family boat', 'Bowrider, deck boat, ski or wake boat'],
        ['pontoon', 'Pontoon or tritoon', 'Wide profile, more wind to hold'],
        ['cruiser_sail', 'Cruiser or sailboat', 'Cabin boat or sailboat'],
      ],
    },
    {
      key: 'length_band',
      title: 'About how long is your boat?',
      hint: 'Close is fine.',
      options: [
        ['under_16', 'Under 16 ft', 'PWC, tender, small skiff'],
        ['ft_16_20', '16–20 ft', 'Most small fishing boats and runabouts'],
        ['ft_21_22', '21–22 ft', 'Larger runabouts and pontoons'],
        ['ft_23_26', '23–26 ft', 'We’ll help confirm the right size'],
      ],
    },
    {
      key: 'bottom',
      title: 'Where do you usually anchor?',
      hint: 'If more than one applies, choose strong current.',
      options: [
        ['sand_mud', 'Mostly sand or firm mud', 'Calm lakes, bays, and coves'],
        ['river_current', 'Strong current', 'Rivers, tidal water, or fast-moving water'],
        ['rock_weeds_mixed', 'Rocks, weeds, or changing bottom', 'Conditions change from spot to spot'],
        ['unknown', 'I’m not sure, it varies', 'We’ll start with the versatile family'],
      ],
    },
    {
      key: 'gear',
      title: 'Do you have the right rope and chain?',
      hint: 'They should be correctly sized and in good condition.',
      options: [
        ['need_everything', 'I need a complete setup', 'Anchor, rope, chain, and connectors'],
        ['anchor_only', 'Yes, mine are ready to use', 'Correct size and in good condition'],
        ['unsure', 'I’m not sure what I have', 'We’ll show both and recommend the safer start'],
      ],
    },
  ];

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function money(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((Number(cents) || 0) / 100);
  }

  class GreenfieldAnchorMatch extends HTMLElement {
    constructor() {
      super();
      this.config = {};
      this.answers = {};
      this.prefill = {};
      this.step = -1;
      this.result = null;
      this.notice = '';
      this.isMoving = false;
    }

    connectedCallback() {
      const configNode = document.getElementById(this.dataset.configId);
      if (configNode) {
        try {
          this.config = JSON.parse(configNode.textContent);
        } catch (error) {
          this.config = {};
        }
      }
      this.restore();
      this.seedFromQuery();
      this.addEventListener('click', this.onClick.bind(this));
      this.render();
    }

    // Entry points elsewhere on the site (the homepage boat-length starter) answer a question
    // up front and pass it in the query string. Values are matched against the options list, so
    // nothing but a real answer can reach state.
    seedFromQuery() {
      this.prefill = {};
      let params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (error) {
        return;
      }

      QUESTIONS.forEach((question) => {
        const value = params.get(question.key);
        if (value && question.options.some((option) => option[0] === value)) {
          this.prefill[question.key] = value;
        }
      });

      if (!Object.keys(this.prefill).length) return;

      // Skip the intro card: the visitor already committed by answering one.
      this.answers = Object.assign({}, this.prefill);
      this.save();
      this.step = this.nextUnanswered(-1);
      if (this.step === QUESTIONS.length) this.result = Engine.recommend(this.answers);
    }

    nextUnanswered(from) {
      for (let i = from + 1; i < QUESTIONS.length; i += 1) {
        if (!this.answers[QUESTIONS[i].key]) return i;
      }
      return QUESTIONS.length;
    }

    restore() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY));
        if (saved && saved.answers) {
          this.savedAnswers = saved.answers;
          this.hasSaved = true;
        }
      } catch (error) {
        this.hasSaved = false;
      }
    }

    save() {
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ answers: this.answers, savedAt: Date.now() }));
      } catch (error) {
        // The chooser still works when storage is blocked.
      }
    }

    onClick(event) {
      const trigger = event.target.closest('[data-action], [data-answer]');
      if (!trigger || this.isMoving) return;

      if (trigger.dataset.answer) {
        const question = QUESTIONS[this.step];
        this.answers[question.key] = trigger.dataset.answer;
        this.save();
        trigger.setAttribute('aria-pressed', 'true');
        trigger.classList.add('is-picked');
        this.move(() => {
          const next = this.nextUnanswered(this.step);
          this.step = next;
          if (next === QUESTIONS.length) this.result = Engine.recommend(this.answers);
        });
        return;
      }

      const action = trigger.dataset.action;
      if (action === 'start') {
        this.answers = Object.assign({}, this.prefill);
        this.step = this.nextUnanswered(-1);
        this.move();
      } else if (action === 'resume') {
        this.answers = Object.assign({}, this.savedAnswers);
        const next = QUESTIONS.findIndex((question) => !this.answers[question.key]);
        this.step = next === -1 ? QUESTIONS.length - 1 : next;
        this.move();
      } else if (action === 'goto') {
        const target = Number(trigger.dataset.step);
        if (Number.isInteger(target) && target >= 0 && target < QUESTIONS.length) {
          this.step = target;
          this.move();
        }
      } else if (action === 'back') {
        if (this.step <= 0) this.step = -1;
        else this.step -= 1;
        this.move();
      } else if (action === 'restart') {
        // A full restart must also drop the deep-linked answer, and clear it from the URL so a
        // reload does not silently seed it again.
        this.prefill = {};
        this.answers = {};
        this.result = null;
        this.step = 0;
        if (window.history && window.history.replaceState && window.location.search) {
          window.history.replaceState({}, '', window.location.pathname);
        }
        this.move();
      }
    }

    move(update) {
      if (this.isMoving) return;
      this.isMoving = true;
      const card = this.querySelector('[data-card]');
      if (card) card.classList.add('is-leaving');
      window.setTimeout(() => {
        if (update) update();
        this.render();
        const nextCard = this.querySelector('[data-card]');
        if (nextCard) {
          nextCard.classList.add('is-entering');
          requestAnimationFrame(() => nextCard.classList.remove('is-entering'));
        }
        this.isMoving = false;
        const section = this.closest('.anchor-match-shell');
        const siteHeader = document.querySelector('.header-wrapper, sticky-header, header.header');
        if (section) {
          const headerHeight = siteHeader ? siteHeader.getBoundingClientRect().height : 0;
          const sectionTop = section.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({ top: Math.max(0, sectionTop - headerHeight), behavior: 'auto' });
        }
        const heading = this.querySelector('[data-focus]');
        if (heading) heading.focus({ preventScroll: true });
      }, 300);
    }

    render() {
      const app = this.querySelector('[data-anchor-match-app]');
      if (!app) return;
      if (this.step === -1) app.innerHTML = this.renderIntro();
      else if (this.step < QUESTIONS.length) app.innerHTML = this.renderQuestion();
      else app.innerHTML = this.renderResult();
    }

    productImages() {
      const p = this.config.products || {};
      return [
        p.mushroom && p.mushroom.anchor,
        p.fluke && p.fluke.anchor,
        p.river && p.river.anchor,
        p.richter && p.richter.anchor,
      ].filter(Boolean);
    }

    renderIntro() {
      const products = this.productImages();
      return `
        <div class="am-card am-card--intro" data-card>
          <div class="am-intro-copy">
            <span class="am-kicker">Greenfield Anchor Match <i>beta</i></span>
            <h1 data-focus tabindex="-1">Find your anchor.</h1>
            <p>Four quick questions. About a minute.</p>
            <button class="am-primary" type="button" data-action="start">Let’s go <span>→</span></button>
            ${this.hasSaved ? '<button class="am-resume" type="button" data-action="resume">Continue where I left off</button>' : ''}
            <a class="am-guide-link" href="${escapeHtml(this.config.routes.boatAnchorsGuide)}">Prefer the full anchor guide?</a>
          </div>
          <div class="am-product-stage" aria-label="Greenfield anchor families">
            <span class="am-stage-word">HOLD</span>
            ${products.map((product, index) => `
              <div class="am-floating-product am-floating-product--${index + 1}">
                <img src="${escapeHtml(product.image)}" alt="" width="420" height="420">
              </div>`).join('')}
            <div class="am-stage-caption">Mushroom · Fluke · River · Richter</div>
          </div>
        </div>`;
    }

    renderQuestion() {
      const question = QUESTIONS[this.step];
      const selected = this.answers[question.key];
      return `
        <div class="am-card am-card--question" data-card>
          <header class="am-topbar">
            <button type="button" data-action="back" aria-label="Go back">←</button>
            <div class="am-progress" aria-label="Question ${this.step + 1} of ${QUESTIONS.length}">
              ${QUESTIONS.map((item, index) => `<span class="${index <= this.step || this.answers[item.key] ? 'is-on' : ''}"></span>`).join('')}
            </div>
            <span class="am-count">${this.answeredCount()} of ${QUESTIONS.length}</span>
          </header>
          ${this.renderAnsweredChips()}
          <div class="am-question-layout">
            ${this.renderVisual(question.key)}
            <main class="am-question">
              <h2 data-focus tabindex="-1">${escapeHtml(question.title)}</h2>
              <p>${escapeHtml(question.hint)}</p>
              <div class="am-options am-options--${escapeHtml(question.key)}">
                ${question.options.map(([value, label, note]) => this.renderOption(question.key, value, label, note, selected === value)).join('')}
              </div>
              ${question.key === 'length_band' ? `<a class="am-small-link" href="${escapeHtml(this.config.routes.contact)}">Boat longer than 26 ft? Ask Greenfield.</a>` : ''}
            </main>
          </div>
          <span class="am-choice-confirmation" aria-hidden="true">Got it, next question</span>
        </div>`;
    }

    answeredCount() {
      return QUESTIONS.filter((question) => this.answers[question.key]).length;
    }

    // Answers carried in from elsewhere (the homepage boat-length starter) are otherwise invisible
    // here, which makes every entry button look like it landed in the same place. Showing them as
    // editable chips proves the choice registered and keeps it changeable.
    renderAnsweredChips() {
      const chips = QUESTIONS.map((question, index) => {
        const value = this.answers[question.key];
        if (!value || index === this.step) return '';
        const option = question.options.find((item) => item[0] === value);
        if (!option) return '';
        return `<button class="am-recap-chip" type="button" data-action="goto" data-step="${index}">
          ${escapeHtml(option[1])}<span aria-hidden="true">Change</span>
        </button>`;
      }).join('');

      if (!chips) return '';
      return `<div class="am-answered" aria-label="Answers so far">${chips}</div>`;
    }

    renderOption(key, value, label, note, selected) {
      let media = '';
      if (key === 'boat_type') media = `<span class="am-boat-shape" data-boat="${escapeHtml(value)}"><i></i></span>`;
      if (key === 'bottom') media = `<span class="am-bottom-swatch" data-bottom="${escapeHtml(value)}"><i></i><b></b></span>`;
      if (key === 'gear') {
        const family = this.previewFamily();
        const products = (this.config.products || {})[family] || {};
        const product = value === 'anchor_only' ? products.anchor : products.kit || products.anchor;
        if (product) media = `<img class="am-gear-image" src="${escapeHtml(product.image)}" alt="" width="180" height="120">`;
      }
      return `
        <button class="am-option ${selected ? 'is-picked' : ''}" type="button" data-answer="${escapeHtml(value)}" aria-pressed="${selected}">
          ${media}
          <span><strong>${escapeHtml(label)}</strong><small>${escapeHtml(note)}</small></span>
          <i class="am-check" aria-hidden="true">✓</i>
        </button>`;
    }

    previewFamily() {
      if (!this.answers.boat_type || !this.answers.length_band || !this.answers.bottom) return 'richter';
      return Engine.recommend(Object.assign({}, this.answers, { gear: 'need_everything' })).primary_family;
    }

    renderVisual(key) {
      if (key === 'boat_type') {
        return `<div class="am-visual am-visual--boat"><span>YOUR BOAT</span><div class="am-waterline"></div><div class="am-hero-boat"><i></i></div></div>`;
      }
      if (key === 'length_band') {
        return `<div class="am-visual am-visual--ruler"><span>ABOUT IS FINE</span><div class="am-ruler">${[10, 12, 14, 16, 18, 20, 22, 24, 26].map((n) => `<i><b>${n}</b></i>`).join('')}</div><strong>ft</strong></div>`;
      }
      if (key === 'bottom') {
        return `<div class="am-visual am-visual--bottom"><span>LOOK DOWN</span><div class="am-drop-line"></div><div class="am-drop-anchor"></div><div class="am-seabed"><i></i><i></i><i></i></div></div>`;
      }
      const family = this.previewFamily();
      const products = (this.config.products || {})[family] || {};
      return `<div class="am-visual am-visual--gear"><span>WHAT’S ALREADY ABOARD?</span>${products.anchor ? `<img src="${escapeHtml(products.anchor.image)}" alt="" width="320" height="320">` : ''}<div class="am-rope-line"></div></div>`;
    }

    answerLabel(key) {
      const question = QUESTIONS.find((item) => item.key === key);
      const option = question && question.options.find(([value]) => value === this.answers[key]);
      return option ? option[1] : '';
    }

    renderAnswerRecap() {
      return `
        <div class="am-answer-recap" aria-label="Your answers">
          ${['boat_type', 'length_band', 'bottom', 'gear'].map((key) => `<span class="am-answer-chip">${escapeHtml(this.answerLabel(key))}</span>`).join('')}
        </div>`;
    }

    renderResultBoundary(result) {
      const largeBoat = result.answers.length_band === 'ft_23_26';
      const noKit = result.caution_codes.includes('COMPLETE_KIT_UNAVAILABLE');
      return `
        <div class="am-result-boundaries">
          <div class="am-result-boundary">
            <strong>${largeBoat ? 'Size confirmation required for this boat.' : 'Confirm the size before buying.'}</strong>
            This result identifies the best-fit anchor family, not the final anchor weight. Use Greenfield’s chart or product options to confirm size.
            <span>For normal attended anchoring. Overnight use, permanent mooring, severe weather, and boats over 26 ft need individual review.</span>
          </div>
          ${noKit ? `<div class="am-result-boundary am-result-boundary--warning">
            <strong>A complete Mushroom kit is not currently listed.</strong>
            We’ll show the anchor, but rope, connectors, and any required chain still need to be matched separately.
          </div>` : ''}
        </div>`;
    }

    renderResult() {
      const result = this.result || Engine.recommend(this.answers);
      const meta = Engine.FAMILY_META[result.primary_family];
      const alt = Engine.FAMILY_META[result.alternative_family];
      const products = (this.config.products || {})[result.primary_family] || {};
      const anchor = products.anchor;
      const kit = products.kit;
      const anchorCard = this.renderProductCard(
        anchor,
        'Anchor only',
        'For boaters with correctly sized rope and chain in good condition.',
        result.recommended_purchase === 'anchor',
        'anchor',
      );
      const kitNote = result.answers.gear === 'unsure'
        ? 'Recommended because you were not sure whether your current rope and chain are suitable.'
        : 'Anchor, rope, chain, and connectors together.';
      const kitCard = kit ? this.renderProductCard(
        kit,
        'Complete setup',
        kitNote,
        result.recommended_purchase === 'kit',
        'kit',
      ) : '';
      const productCards = result.recommended_purchase === 'kit'
        ? `${kitCard}${anchorCard}`
        : `${anchorCard}${kitCard}`;
      return `
        <div class="am-card am-card--result" data-card>
          <header class="am-result-heading">
            <button type="button" data-action="restart">Start over</button>
            <div>
              <span>Your best-fit anchor family</span>
              <h2 data-focus tabindex="-1">Start with a ${escapeHtml(meta.label)} anchor.</h2>
              <p>${escapeHtml(meta.line)}</p>
              ${this.renderAnswerRecap()}
            </div>
            <div class="am-family-stamp">${escapeHtml(meta.label)}</div>
          </header>
          ${this.renderResultBoundary(result)}
          ${this.notice ? `<div class="am-notice" role="status">${escapeHtml(this.notice)}</div>` : ''}
          <div class="am-result-body">
            <section class="am-result-why">
              <div class="am-result-product">
                ${anchor ? `<img src="${escapeHtml(anchor.image)}" alt="${escapeHtml(anchor.imageAlt || anchor.shortTitle)}" width="520" height="520">` : ''}
              </div>
              <div>
                <span>Why this one</span>
                <h3>${escapeHtml(meta.use)}</h3>
                <p>${escapeHtml(this.resultReason(result))}</p>
                <a href="${escapeHtml(this.config.routes.sizeChartGuide)}">Check the Greenfield size chart</a>
              </div>
            </section>
            <section class="am-shop-choices">
              <div class="am-shop-title">
                <span>Choose your setup</span>
                <p>Confirm the final size on the product page.</p>
              </div>
              <div class="am-product-pair">
                ${productCards}
              </div>
            </section>
          </div>
          <footer class="am-result-footer">
            <div><span>If the bottom changes</span><strong>Look at the ${escapeHtml(alt.label)} instead.</strong><p>${escapeHtml(alt.line)}</p></div>
            <div><span>Before you drop it</span><strong>Use chain and let out enough line.</strong><a href="${escapeHtml(this.config.routes.safeAnchoringGuide)}">See the short anchoring guide →</a></div>
          </footer>
        </div>`;
    }

    renderProductCard(product, title, note, picked, kind) {
      if (!product) return '';
      const price = product.priceFormatted || money(product.price);
      const unavailable = !product.available;
      const actionLabel = kind === 'kit' ? 'Confirm size and view kit' : 'View size options';
      return `
        <article class="am-product-card ${picked ? 'is-recommended' : ''}">
          ${picked ? '<span class="am-recommended-label">Recommended setup</span>' : ''}
          <div class="am-product-image"><img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.imageAlt || product.shortTitle)}" width="260" height="180"></div>
          <div class="am-product-copy">
            <h3>${escapeHtml(title)}</h3>
            <p>${escapeHtml(note)}</p>
            <strong>From ${escapeHtml(price)}</strong>
          </div>
          ${unavailable
            ? `${product.walmartUrl ? `<a class="am-product-cta am-product-cta--retailer" href="${escapeHtml(product.walmartUrl)}" target="_blank" rel="nofollow sponsored noopener">${escapeHtml(product.walmartLabel || 'Check Walmart')} ↗</a>` : ''}<a class="am-product-secondary" href="${escapeHtml(product.url)}">See this anchor</a>`
            : `<a class="am-product-cta" href="${escapeHtml(product.url)}">${escapeHtml(actionLabel)}</a><a class="am-product-secondary" href="${escapeHtml(this.config.routes.sizeChartGuide)}">Check the size chart</a>`}
        </article>`;
    }

    resultReason(result) {
      const boat = this.answerLabel('boat_type').toLowerCase();
      const length = this.answerLabel('length_band');
      if (result.primary_family === 'mushroom') return `For your ${length} ${boat}, a compact anchor is easier to handle and store in calm, soft-bottom conditions.`;
      if (result.primary_family === 'fluke') return `For your ${length} ${boat}, broad flukes are a strong fit for sand and firm mud. Use the confirmed size with suitable chain.`;
      if (result.primary_family === 'river') return `For your ${length} ${boat}, the River family is built to grab in current and around structure. Final size still depends on the boat and conditions.`;
      if (result.answers.bottom === 'unknown') return `Because the bottom varies or is unknown, the versatile Richter family is the safer starting point than a narrow specialist.`;
      return `For your ${length} ${boat}, the Richter family is Greenfield’s versatile choice for rocks, weeds, mud, sand, and changing bottoms.`;
    }
  }

  customElements.define('greenfield-anchor-match', GreenfieldAnchorMatch);
})();
